import React, { useEffect, useState } from 'react';
import { FieldArray, useFormik } from 'formik';
import { Values } from './AddMerchantCashAdvance';
import { parseMcaDisplayTiers } from '../../utils/mca';
import { Button, Divider, InputLabel, List, Stack, TextField } from '@mui/material';
import { PlusCircleOutlined } from '@ant-design/icons';
import { McaAutocomplete } from './McaAutocomplete';
import { FundingFee, McaDisplayAttribute, McaRefi, McaRefiType, MerchantCashAdvance } from '../../graphql/types';
import { capitalizeFirstLetter } from '../../utils/strings';
import McaRefisListOption, { getRefiTypeAmount } from './McaRefisListOption';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import CurrencyFormatterWithoutPrefix from '../../components/input-formatters/CurrencyFormatterWithoutPrefix';
import { v4 as uuid4 } from 'uuid';

export interface Props {
  disabled?: boolean;
  formik: ReturnType<typeof useFormik<Values>>;
  setMcaFees: React.Dispath<React.SetStateAction<FundingFee[]>>;
  mcaDisplayAttributes: McaDisplayAttribute[];
  initialMca?: MerchantCashAdvance | null;
}

export const SetMcaRefis = ({ formik, disabled = false, setMcaFees, mcaDisplayAttributes, initialMca }: Props) => {
  const refis = formik.values.refis || [];
  const [mca, setMca] = useState<MerchantCashAdvance | null>(initialMca || null);
  const [type, setType] = useState<McaRefiType>(McaRefiType.ClearedBalance);
  const [refiAmount, setRefiAmount] = useState<number>(0);
  const [showAdd, setShowAdd] = useState(!refis?.length);
  const handleAddRefi = () => {
    setShowAdd(true);
  };
  const handleSetRefi = () => {
    if (!mca) return;
    formik.setFieldValue('refis', [
      ...refis.filter((f) => f.id !== mca.id),
      {
        id: mca.id,
        number: mca.number,
        referenceMca: mca.referenceMca,
        pendingBalance: mca.balancePnd,
        totalFees: mca.totalfines,
        clearedBalance: mca.balanceclr,
        type: type,
        merchantId: mca.merchant?.id,
        merchantLogo: mca.merchant?.logo,
        merchantLegalName: mca.merchant?.legalName,
        amount: refiAmount
      }
    ]);
    setShowAdd(false);

    if (!formik.values.merchant?.id && mca.merchant?.id) {
      formik.setFieldValue('merchant', mca.merchant);
    }
    if (!formik.values.broker?.id && mca.broker?.id) {
      formik.setFieldValue('broker', mca.broker);
    }
    if (!formik.values.manager?.id && mca.manager?.id) {
      formik.setFieldValue('manager', mca.manager);
    }
    if (!formik.values.company?.id && mca.company?.id) {
      formik.setFieldValue('company', mca.company);
    }    
    const priorMcaRefiTier = parseMcaDisplayTiers(mca.displayAttributes || [], mcaDisplayAttributes).refi || 0;
    formik.setFieldValue('refiLevel', priorMcaRefiTier + 1);
    const newFeeId = uuid4();
    const comment = `REFI Balance #${mca.number}`;

    const newFee = {
      id: newFeeId,
      amount: refiAmount,
      comment,
      config: {},
      number: mca.number
    };

    setMcaFees((prevFees) => [...prevFees, newFee]);
    const fundingAmount = formik.values.amount || 0;
    formik.setFieldValue(`fees.${newFeeId}`, {
      amount: refiAmount,
      percents: fundingAmount ? (refiAmount / fundingAmount) * 100 : 0,
      comment,
      config: {}
    });
  };

  const handleDeleteRefi = (refi: McaRefi) => {
    const remaining = refis.filter((f) => f.id !== refi.id);
    formik.setFieldValue('refis', remaining);
    formik.setFieldValue('refiLevel', remaining.length > 0 ? remaining.length : null);
    setMcaFees((prevFees) => {
      const updatedFees = prevFees.filter((fee) => fee.number !== refi.number);
      return updatedFees;
    });
  };

  useEffect(() => {
    if (!mca) {
      setRefiAmount(0);
    } else {
      setRefiAmount(getRefiTypeAmount(type, mca));
    }
  }, [type, mca]);

  return (
    <Stack>
      <Stack>
        <FieldArray
          name={'refis'}
          children={(helpers) => (
            <List component="nav" aria-label="Merchant profile" sx={{ py: 0, '& .MuiListItem-root': { p: 0 } }}>
              {refis.map((refi) => (
                <McaRefisListOption key={refi.id} disabled={disabled} onDelete={handleDeleteRefi} refi={refi} />
              ))}
            </List>
          )}
        />
      </Stack>
      {!showAdd && !disabled && (
        <Stack sx={{ padding: 2 }} alignItems="flex-start">
          <Button color="primary" variant={'text'} size={'small'} startIcon={<PlusCircleOutlined />} onClick={handleAddRefi}>
            Add Payoff
          </Button>
        </Stack>
      )}
      {showAdd && !disabled && (
        <Stack>
          {!!refis?.length && (
            <Stack sx={{ padding: 2 }}>
              <Divider />
            </Stack>
          )}
          <McaAutocomplete setMca={setMca} initialValue={initialMca} />
          {mca && (
            <>
              <Stack sx={{ padding: 2 }} spacing={1.25}>
                <InputLabel required={true}>Balance</InputLabel>
                <TextField
                  fullWidth
                  size={'small'}
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value as McaRefiType);
                  }}
                  disabled={!mca}
                  select
                  SelectProps={{ native: true }}
                >
                  {Object.values(McaRefiType).map((key) => (
                    <option key={key} value={key}>
                      {capitalizeFirstLetter(key).replace(' balance', '')} <CurrencyText value={getRefiTypeAmount(key, mca)} />
                    </option>
                  ))}
                </TextField>
                <TextField
                  size={'small'}
                  fullWidth
                  disabled={!mca}
                  placeholder="Refi Amount"
                  value={refiAmount}
                  onChange={(e) => {
                    setRefiAmount(parseFloat(e.target.value));
                  }}
                  InputProps={{
                    startAdornment: '$',
                    inputComponent: CurrencyFormatterWithoutPrefix as any,
                    inputProps: { min: 1 }
                  }}
                />
              </Stack>
              <Stack sx={{ padding: 2 }} direction={'row'} spacing={1.25} alignItems="center" justifyContent="flex-end">
                <Button
                  size={'extraSmall'}
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setShowAdd(false);
                  }}
                >
                  Cancel
                </Button>
                <Button size={'extraSmall'} disabled={!refiAmount} variant="contained" color="primary" onClick={handleSetRefi}>
                  Confirm
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};